// In-app card payment — web (in-app-card-payment spec).
//
// A modal that collects a card / Apple Pay / Google Pay for ONE pay-at-store Stripe
// order, mirroring the mobile PaymentSheet flow (admin-mobile-app/App.js
// onChargeCardInApp). The backend is client-agnostic: this reuses the SAME two endpoints
// the mobile app calls —
//   POST /admin/pos/payments/stripe/intent   -> { clientSecret, publishableKey, paymentIntentId, amount, currency }
//   POST /admin/pos/payments/stripe/confirm  -> { ok, paid, status }
// The amount is NEVER sent; the server re-prices from the order.
//
// Stripe.js is loaded LAZILY (dynamic import inside an effect), so the POS queue bundle
// does not pull the SDK for tenants that never charge a card, and so this module and the
// queue stay importable in jest without the dependency present.
//
// Card entry works in every browser. Apple Pay / Google Pay wallet buttons render inside
// the Payment Element only when the browser supports them (Safari on a Mac/iPhone for
// Apple Pay; Chrome for Google Pay over HTTPS) — the one capability that genuinely cannot
// cross from mobile, since a laptop has no NFC card reader for tap-to-pay.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Auth } from 'aws-amplify';
import { friendlyApiError } from '../utils/apiError';
import { formatMinor } from '../utils/money';

const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';

// Create the PaymentIntent server-side. Amount is deliberately NOT sent — the server
// re-prices from the order so a client cannot influence what is charged. Mirrors the
// mobile adminApi.createStripePaymentIntent request/response shape exactly.
async function createStripePaymentIntent({ token, orderNumber, orderTimestamp }) {
  const res = await fetch(`${API_BASE_URL}/admin/pos/payments/stripe/intent`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ order_number: orderNumber, order_timestamp: orderTimestamp }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to start card payment'));
  return data;
}

// Confirm server-side after Stripe.js reports success. The server RE-FETCHES the intent
// from Stripe (never trusts the client) and marks the order paid only when it is
// genuinely 'succeeded'. Idempotent — safe to call even if the webhook already settled.
async function confirmStripePayment({ token, orderNumber, orderTimestamp, paymentIntentId }) {
  const res = await fetch(`${API_BASE_URL}/admin/pos/payments/stripe/confirm`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      order_number: orderNumber,
      order_timestamp: orderTimestamp,
      payment_intent_id: paymentIntentId,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to confirm card payment'));
  return data;
}

// Inner form. Rendered only once Stripe.js + Elements are loaded and a clientSecret
// exists, so the hooks it needs (useStripe/useElements) always have a provider above.
function ChargeCardForm({ stripeHooks, orderNumber, orderTimestamp, paymentIntentId, amountLabel, onPaid, onClose }) {
  const { useStripe, useElements, PaymentElement } = stripeHooks;
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      // 1. Collect + confirm the card/wallet in the browser. redirect: 'if_required'
      //    keeps the whole flow in-page for card + Apple/Google Pay (no hosted redirect).
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });
      if (confirmError) {
        // A cancelled wallet sheet or a declined card surfaces here. The order simply
        // stays unpaid and staff can retry or use another settle action.
        setError(confirmError.message || 'Card payment failed.');
        return;
      }
      if (!paymentIntent || paymentIntent.status !== 'succeeded') {
        setError('Payment was not completed. The order remains unpaid.');
        return;
      }

      // 2. Confirm server-side — the server re-fetches the intent from Stripe and marks
      //    the order paid only on a genuine 'succeeded'. Never trusts this browser.
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const confirmRes = await confirmStripePayment({
        token,
        orderNumber,
        orderTimestamp,
        paymentIntentId,
      });
      onPaid(confirmRes?.paid === true);
    } catch (err) {
      setError(err?.message || 'Card payment failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement onReady={() => setReady(true)} />
      {error ? (
        <div className="field-hint" style={{ marginTop: 12, color: 'rgba(153,27,27,0.95)' }}>
          {error}
        </div>
      ) : null}
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button
          type="submit"
          className="btn-primary"
          disabled={!stripe || !ready || submitting}
          style={{ flex: 1 }}
        >
          {submitting ? 'Charging…' : `Charge ${amountLabel}`}
        </button>
        <button
          type="button"
          className="btn-secondary"
          onClick={onClose}
          disabled={submitting}
          style={{ flex: 1 }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function StripeChargeCardModal({ order, onClose, onPaid }) {
  const orderNumber = String(order?.order_number || '').trim();
  const orderTimestamp = order?.order_timestamp;

  // 'loading' -> 'ready' -> (form handles the rest) | 'error'
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const [stripeHooks, setStripeHooks] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [amountLabel, setAmountLabel] = useState('');
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    let cancelled = false;

    (async () => {
      try {
        if (!API_BASE_URL) throw new Error('Missing API base URL (REACT_APP_ADMIN_API_URL).');
        if (!orderNumber || !orderTimestamp) throw new Error('Missing order identifiers.');

        // 1. Server creates the PaymentIntent (amount re-priced server-side).
        const session = await Auth.currentSession();
        const token = session.getIdToken().getJwtToken();
        const intent = await createStripePaymentIntent({ token, orderNumber, orderTimestamp });
        const secret = String(intent?.clientSecret || '').trim();
        const pubKey = String(intent?.publishableKey || '').trim();
        const piId = String(intent?.paymentIntentId || '').trim();
        if (!secret || !pubKey) throw new Error('Card payment could not be started.');

        // 2. Lazily load Stripe.js + the React bindings. Dynamic import keeps the SDK out
        //    of the main bundle and out of jest.
        const [{ loadStripe }, reactStripe] = await Promise.all([
          import('@stripe/stripe-js'),
          import('@stripe/react-stripe-js'),
        ]);
        if (cancelled) return;

        const amountMinor = Number(intent?.amount);
        const currency = String(intent?.currency || '').toUpperCase();
        setAmountLabel(
          Number.isFinite(amountMinor)
            ? (currency ? formatMinor(amountMinor, currency) : formatMinor(amountMinor))
            : 'card',
        );
        setPaymentIntentId(piId);
        setClientSecret(secret);
        setStripePromise(loadStripe(pubKey));
        setStripeHooks({
          Elements: reactStripe.Elements,
          PaymentElement: reactStripe.PaymentElement,
          useStripe: reactStripe.useStripe,
          useElements: reactStripe.useElements,
        });
        setPhase('ready');
      } catch (err) {
        if (cancelled) return;
        // A build without the Stripe deps installed lands here (import throws) — surface
        // a clear message rather than a blank modal.
        const msg = /Cannot find module|Failed to fetch dynamically imported/.test(String(err?.message || ''))
          ? 'Card payment is unavailable in this build.'
          : (err?.message || 'Could not start card payment.');
        setError(msg);
        setPhase('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [orderNumber, orderTimestamp]);

  const elementsOptions = useMemo(
    () => (clientSecret ? { clientSecret, appearance: { theme: 'stripe' } } : null),
    [clientSecret],
  );

  const Elements = stripeHooks?.Elements;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Charge card for order ${orderNumber}`}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: '#fff', borderRadius: 12, padding: 20, width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Charge card</h3>
          <span className="field-hint">Order {orderNumber}</span>
        </div>

        {phase === 'loading' ? (
          <div className="field-hint" style={{ padding: '16px 0' }}>Starting card payment…</div>
        ) : null}

        {phase === 'error' ? (
          <>
            <div className="field-hint" style={{ color: 'rgba(153,27,27,0.95)', padding: '8px 0' }}>{error}</div>
            <button type="button" className="btn-secondary" onClick={onClose} style={{ width: '100%', marginTop: 8 }}>
              Close
            </button>
          </>
        ) : null}

        {phase === 'ready' && Elements && elementsOptions && stripePromise ? (
          <Elements stripe={stripePromise} options={elementsOptions}>
            <ChargeCardForm
              stripeHooks={stripeHooks}
              orderNumber={orderNumber}
              orderTimestamp={orderTimestamp}
              paymentIntentId={paymentIntentId}
              amountLabel={amountLabel}
              onPaid={onPaid}
              onClose={onClose}
            />
          </Elements>
        ) : null}
      </div>
    </div>
  );
}
